import { Modal } from '../../../../customComponents/Modal'
import { StartLeadershipDayAndConferenceType } from '../../../../../models/StartLeadershipDayAndConferenceType'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, FormControl, InputAdornment, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material'
import { AttachMoney } from '@mui/icons-material'
import {
  AddStartLeadershipDayAndConference,
  UpdateStartLeadershipDayAndConference
} from '../../../../../services/AnnualPlanService'
import { ActivityEditSession } from '../ActivityEditSession'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'

export interface AddStartLeadershipDayAndConferenceFormValues {
  priorityId: number | null
  eventType: StartLeadershipDayAndConferenceType
  numberOfAttendees: number | null
  budgetRequest: number | null
  isImplementationPhase?: boolean
}

export interface UpdateStartLeadershipDayAndConferenceFormValues extends AddStartLeadershipDayAndConferenceFormValues {
  id: number
  isUpdate: boolean
}

interface Props {
  open: boolean
  priorityId: number
  initialValues?: Record<StartLeadershipDayAndConferenceType, UpdateStartLeadershipDayAndConferenceFormValues>
  isImplementationPhase?: boolean
  onClose: () => void
  onSaved: () => void
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const StartLeadershipDayAndConferenceModal = (props: Props): JSX.Element => {
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [formValues, setFormValues] = useState<Record<StartLeadershipDayAndConferenceType, AddStartLeadershipDayAndConferenceFormValues>>({
    [StartLeadershipDayAndConferenceType.FallLeadership]: {
      priorityId: props.priorityId ?? 0,
      eventType: StartLeadershipDayAndConferenceType.FallLeadership,
      numberOfAttendees: null,
      budgetRequest: null,
      isImplementationPhase: props.isImplementationPhase ?? false
    },
    [StartLeadershipDayAndConferenceType.SpringLeadershipDay]: {
      priorityId: props.priorityId ?? 0,
      eventType: StartLeadershipDayAndConferenceType.SpringLeadershipDay,
      numberOfAttendees: null,
      budgetRequest: null,
      isImplementationPhase: props.isImplementationPhase ?? false
    },
    [StartLeadershipDayAndConferenceType.STARTConference]: {
      priorityId: props.priorityId ?? 0,
      eventType: StartLeadershipDayAndConferenceType.STARTConference,
      numberOfAttendees: null,
      budgetRequest: null,
      isImplementationPhase: props.isImplementationPhase ?? false
    }
  })
  const [submitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues(props.initialValues)
    }
  }, [props.initialValues])

  const hasInvalidFormValues = Object.values(formValues)
    .some((formValue) => formValue.eventType == null ||
      formValue.numberOfAttendees == null ||
      formValue.budgetRequest == null ||
      formValue.numberOfAttendees.toString() === '' ||
      formValue.budgetRequest.toString() === '')

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: StartLeadershipDayAndConferenceType): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [key]: { ...formValues[key], [name]: value } })
  }

  const checkThreshold = (): boolean => {
    if (props.initialValues == null) return false

    return Object.values(StartLeadershipDayAndConferenceType).some(eventType => {
      const initialBudget = props.initialValues?.[eventType]?.budgetRequest
      const currentBudget = formValues[eventType]?.budgetRequest

      // Skip comparison if either value is null/undefined
      if (initialBudget == null || currentBudget == null) return false

      // Convert to numbers and skip if initial budget is 0 (avoid division by zero)
      const initialAmount = Number(initialBudget)
      const currentAmount = Number(currentBudget)

      if (initialAmount === 0 && currentAmount !== initialAmount) return true

      // Calculate percentage change
      const changePercent = Math.abs(currentAmount - initialAmount) / initialAmount
      return changePercent > 0.1 // 10% threshold
    })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.initialValues != null &&
      !props.initialValues[StartLeadershipDayAndConferenceType.FallLeadership]?.isUpdate &&
      checkThreshold()
    ) {
      setConfirmState('needsConfirmation')
      return
    }

    await continueSubmit()
  }

  const continueSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields. If an event is not needed, enter 0.')
      return
    }

    const updateFormValues: Record<string, UpdateStartLeadershipDayAndConferenceFormValues> = {
      FallLeadership: {
        id: props.initialValues?.[StartLeadershipDayAndConferenceType.FallLeadership].id ?? 0,
        isImplementationPhase: props.isImplementationPhase ?? false,
        isUpdate: props.initialValues?.[StartLeadershipDayAndConferenceType.FallLeadership].isUpdate ?? false,
        ...formValues[StartLeadershipDayAndConferenceType.FallLeadership]
      },
      SpringLeadershipDay: {
        ...formValues[StartLeadershipDayAndConferenceType.SpringLeadershipDay],
        isImplementationPhase: props.isImplementationPhase ?? false,
        isUpdate: props.initialValues?.[StartLeadershipDayAndConferenceType.SpringLeadershipDay].isUpdate ?? false,
        id: props.initialValues?.[StartLeadershipDayAndConferenceType.SpringLeadershipDay].id ?? 0
      },
      StartConference: {
        ...formValues[StartLeadershipDayAndConferenceType.STARTConference],
        isImplementationPhase: props.isImplementationPhase ?? false,
        isUpdate: props.initialValues?.[StartLeadershipDayAndConferenceType.STARTConference].isUpdate ?? false,
        id: props.initialValues?.[StartLeadershipDayAndConferenceType.STARTConference].id ?? 0
      }
    }

    const addFormValues: Record<string, AddStartLeadershipDayAndConferenceFormValues> = {
      FallLeadership: {
        ...formValues[StartLeadershipDayAndConferenceType.FallLeadership],
        isImplementationPhase: props.isImplementationPhase ?? false
      },
      SpringLeadershipDay: {
        ...formValues[StartLeadershipDayAndConferenceType.SpringLeadershipDay],
        isImplementationPhase: props.isImplementationPhase ?? false
      },
      STARTConference: {
        ...formValues[StartLeadershipDayAndConferenceType.STARTConference],
        isImplementationPhase: props.isImplementationPhase ?? false
      }
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateStartLeadershipDayAndConference(updateFormValues)
      : await AddStartLeadershipDayAndConference(addFormValues)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    toast.success(`Attendance Plan ${props.initialValues != null ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        title={props?.initialValues != null ? 'Update START Leadership Days and Conference' : 'Add START Leadership Days and Conference'}
        open={props.open}
        data-testid={props?.initialValues != null ? 'editStartLeadershipDayAndConferenceModal' : 'addStartLeadershipDayAndConferenceModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Typography variant='h5'>START Leadership Days and Conference</Typography>
            <Typography variant='body1'>Please enter the number of attendees and budget request for each event.</Typography>

            {Object.keys(formValues).map(key => {
              const eventFormValues = formValues[key as StartLeadershipDayAndConferenceType]
              const numberOfAttendeesInvalid = eventFormValues.numberOfAttendees == null ||
                eventFormValues.numberOfAttendees < 0 ||
                eventFormValues.numberOfAttendees.toString() === ''
              const budgetRequestInvalid = eventFormValues.budgetRequest == null ||
                eventFormValues.budgetRequest < 0 ||
                eventFormValues.budgetRequest.toString() === ''

              return <Box key={key} data-testid='leadershipDayAndConferenceForm'>
                <Typography variant='body1'>{key}</Typography>

                <Box sx={{ display: 'flex', gap: '16px' }}>
                  <TextField
                    name='numberOfAttendees'
                    label='Number Attending'
                    value={eventFormValues.numberOfAttendees ?? ''}
                    data-testid='numberOfAttendees'
                    onChange={e => { handleFieldUpdate(e, key as StartLeadershipDayAndConferenceType) }}
                    variant='filled'
                    required={eventFormValues.numberOfAttendees == null}
                    error={submitted && numberOfAttendeesInvalid}
                  />

                  <FormControl sx={{ width: 'fit-content' }} variant="outlined">
                    <InputLabel htmlFor="budget-request">Budget Request</InputLabel>
                    <OutlinedInput
                      id="budget-request"
                      startAdornment={
                        <InputAdornment position="start">
                          <AttachMoney />
                        </InputAdornment>
                      }
                      name='budgetRequest'
                      label="Budget Request"
                      value={eventFormValues.budgetRequest ?? ''}
                      data-testid='budgetRequest'
                      onChange={e => { handleFieldUpdate(e, key as StartLeadershipDayAndConferenceType) }}
                      required={eventFormValues.budgetRequest == null}
                      error={submitted && budgetRequestInvalid}
                    />
                  </FormControl>
                </Box>
              </Box>
            })}

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues[StartLeadershipDayAndConferenceType.FallLeadership].id}
                activityName='START Leadership Day and Conference'
                onClose={props.onClose}
              />
            }
          </Box>
        }
    />
  )
}
